
'use client';

import LandingLayout from '@/components/LandingLayout';
import { hardscapeContractorsFaqs } from '@/data/industryFaqs';
import { trackButtonClick, trackPageView, trackPhoneClick } from '@/lib/analytics';
import {
  ArrowRightIcon,
  CalendarIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ClockIcon,
  DocumentTextIcon,
  PhoneIcon,
  SparklesIcon,
  StarIcon,
  UserGroupIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContextNext';
import { getPlanDisplayPrice, PRICING_PLANS_RECORD } from '../../types/pricing';

export default function HardscapeContractorsPage() {
  useEffect(() => {
    trackPageView('Hardscape Contractors Landing', undefined, { funnel_step: 'industry_landing' });
  }, []);

  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const { isAuthenticated, isInitialized } = useAuth();
  const pricingContext = { isAuthenticated: !isInitialized || isAuthenticated };
  const growthDisplayPrice = getPlanDisplayPrice(PRICING_PLANS_RECORD.growth, pricingContext);

  const hardscapeSpecificBenefits = [
    {
      title: "Capture Patio & Deck Design Quotes",
      description: "When homeowners are planning that $15,000+ deck build, make sure YOU answer first. Greet schedules consultations automatically.",
      icon: PhoneIcon,
      metric: "High-value jobs"
    },
    {
      title: "Never Miss Urgent Projects",
      description: "Customers with failed patios or damaged stonework often call multiple contractors. Be the first to respond.",
      icon: ClockIcon,
      metric: "Instant response"
    },
    {
      title: "Qualify Before You Quote",
      description: "Greet collects project details, budget, and timeline before your consultation. You show up pre-qualified.",
      icon: DocumentTextIcon,
      metric: "Pre-qualified leads"
    },
    {
      title: "24/7 Availability",
      description: "Customers planning outdoor projects often research and call on evenings and weekends. Your AI never sleeps.",
      icon: UserGroupIcon,
      metric: "Always answering"
    },
    {
      title: "Survive Booking Voicemail Hell",
      description: "No more voicemail ping-pong. Appointments get confirmed and added to your calendar instantly.",
      icon: CalendarIcon,
      metric: "Zero confusion"
    },
    {
      title: "Multiple Project Type Handling",
      description: "Whether it's deck building, patio stone, retaining walls, or landscape lighting-Greet knows what to ask.",
      icon: SparklesIcon,
      metric: "All project types"
    }
  ];

  const scenarios = [
    {
      title: "The $25,000 Patio Project That Called Your Competitor",
      problem: "Tuesday at 2 PM: Homeowner researching deck materials calls your number, gets voicemail. Calls your competitor. Three weeks later, competitor is building their dream deck.",
      solution: "With Greet: Homeowner gets instant friendly response, project details are collected, consultation scheduled for tomorrow. You're closing the deal."
    },
    {
      title: "Spring Patio Season Explosion",
      problem: "April-May: 60+ calls per week, but you're managing crews and can only handle 20 personally. Losing 50%+ of calls to voicemail or delayed responses.",
      solution: "With Greet: Every call answered instantly. Routine questions handled automatically. Only serious prospects requiring consultation get your attention. Calendar fills up automatically."
    },
    {
      title: "The Emergency Repair Call",
      problem: "Friday evening: Patio from competitor is failing, homeowner wants emergency repair. Calls you, gets nothing. Finds another contractor by Saturday morning.",
      solution: "With Greet: Call answered immediately, assessed as emergency, you get instant notification. You're first to bid on the fix and potentially upsell a full patio redesign."
    }
  ];

  const testimonials = [
    {
      name: "David Chen",
      company: "Premier Hardscapes LLC",
      quote: "We specialize in high-end deck and patio builds-projects averaging $20,000+. Greet ensures we never miss a single inquiry. We went from missing 25% of calls to answering 100%. Game changer.",
      stat: "Closed 15 additional projects"
    },
    {
      name: "Lisa Kowalski",
      company: "Stone & Soil Contractors",
      quote: "As a woman-owned hardscape company, we're competing against bigger contractors. Greet gives us that professional edge-customers think we have a full office team. Our response time now beats everyone.",
      stat: "40% more consultations booked"
    },
    {
      name: "Mark Stefanelli",
      company: "Elite Outdoor Living Co.",
      quote: "Our retaining wall projects are high-margin work. Missing even one call costs us thousands. Greet's emergency detection means we're always first when someone's walls are failing. It's paid for itself 10x over.",
      stat: "$120K in emergency repair work"
    }
  ];

  return (
    <LandingLayout>
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-slate-900 via-amber-900 to-slate-900 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-red-500/10"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 blur-3xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="max-w-4xl">
            <div className="mb-6 inline-flex items-center gap-3 rounded-full bg-amber-500/20 border border-amber-400/30 px-4 py-2 text-sm font-semibold text-amber-100">
              <span className="h-2 w-2 rounded-full bg-amber-400"></span>
              #1 AI Answering Service for Hardscape Contractors
            </div>
            
            <h1 className="text-5xl md:text-7xl font-extrabold bg-gradient-to-r from-white via-amber-100 to-orange-200 bg-clip-text text-transparent pb-8 leading-tight">
              Stop Missing High-Ticket Hardscape Calls
            </h1>
            
            <p className="text-xl md:text-2xl text-amber-50 mb-8 leading-relaxed max-w-2xl">
              AI-powered call answering that captures deck quotes, patio inquiries, and emergency repair calls-so you never lose a $10,000+ project to a competitor who answered faster.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <Link
                href="/register"
                onClick={() => trackButtonClick("hardscape_hero_trial", "hardscape_page")}
                className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white px-10 py-5 rounded-2xl font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-amber-500/25 inline-flex items-center justify-center"
              >
                Start Free Trial Now
                <ArrowRightIcon className="h-5 w-5 ml-2" />
              </Link>
              <a
                href="tel:+14158773166"
                onClick={() => trackPhoneClick('+14158773166', 'hardscape_hero_demo')}
                className="bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 px-10 py-5 rounded-2xl font-bold text-lg transition-all duration-300 inline-flex items-center hover:scale-105"
              >
                See Live Demo
                <ArrowRightIcon className="h-5 w-5 ml-2" />
              </a>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6">
                <div className="text-3xl font-bold text-white mb-1">100%</div>
                <div className="text-sm text-amber-100">Call Capture</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6">
                <div className="text-3xl font-bold text-white mb-1">$15K+</div>
                <div className="text-sm text-amber-100">Avg Project Value</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6">
                <div className="text-3xl font-bold text-white mb-1">24/7</div>
                <div className="text-sm text-amber-100">Availability</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6">
                <div className="text-3xl font-bold text-white mb-1">15 min</div>
                <div className="text-sm text-amber-100">Setup Time</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              High-Ticket Projects Calling Your Competitors
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A homeowner calls three contractors about a $20,000 patio project. First one to answer gets the job. Who are they calling?
            </p>
          </div>

          {scenarios.map((scenario, index) => (
            <div key={index} className="mb-8 bg-gradient-to-r from-gray-50 to-amber-50 rounded-2xl p-8 border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">{scenario.title}</h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <XCircleIcon className="h-6 w-6 text-red-600" />
                    <p className="font-semibold text-gray-900">Your Reality</p>
                  </div>
                  <p className="text-gray-700">{scenario.problem}</p>
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <CheckCircleIcon className="h-6 w-6 text-amber-600" />
                    <p className="font-semibold text-gray-900">With Greet</p>
                  </div>
                  <p className="text-gray-700">{scenario.solution}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Solution - Hardscape Specific */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Greet for Hardscape Contractors
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Built specifically to help you capture every deck, patio, and hardscape quote
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {hardscapeSpecificBenefits.map((benefit, index) => {
              const Icon = benefit.icon;
              const colors = [
                'from-amber-500 to-orange-600',
                'from-orange-500 to-red-600',
                'from-red-500 to-pink-600',
                'from-pink-500 to-rose-600',
                'from-amber-600 to-amber-700',
                'from-orange-600 to-orange-700'
              ];
              return (
                <div key={index} className="bg-white rounded-2xl border border-gray-100 p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group">
                  <div className={`w-16 h-16 rounded-xl bg-gradient-to-r ${colors[index]} flex items-center justify-center mb-6 shadow-lg transition-transform duration-300 group-hover:scale-110`}>
                    <Icon className="h-8 w-8 text-white transition-transform duration-300 group-hover:scale-125" />
                  </div>
                  <div className="inline-block px-4 py-1 bg-amber-50 rounded-full mb-4 transition-all duration-300 group-hover:bg-amber-100 group-hover:scale-105">
                    <span className="text-sm font-bold text-amber-700 transition-colors duration-300 group-hover:text-amber-900">{benefit.metric}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 transition-colors duration-300 group-hover:text-amber-600">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed transition-colors duration-300 group-hover:text-gray-700">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Real Scenarios */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              How Hardscape Contractors Use Greet
            </h2>
          </div>

          <div className="space-y-8">
            {[
              {
                title: "Design Consultation Capture",
                description: "Homeowner calls about a custom deck design. Greet collects: project type, estimated size, material preferences, timeline, and budget. By the time your consultation call comes, you know exactly if this is a qualified lead and what they want."
              },
              {
                title: "Emergency Retaining Wall Repair",
                description: "A failing retaining wall is losing a customer's yard. They call 4 contractors in a row. Greet flags this as emergency, alerts you immediately. You respond first with a repair bid + upsell for full landscape rebuild."
              },
              {
                title: "Multi-Property Consultation",
                description: "High-net-worth customer wants both a front patio redesign AND back deck expansion. Greet collects details on both projects, books two consultations. You arrive prepared and ready to close both jobs."
              },
              {
                title: "Material & Timeline Pre-Qualification",
                description: "Budget shopper calls asking about cost for a 20x20 paver patio. Greet quickly establishes budget range. If they're tire-kickers ($2K budget for $15K job), you don't waste time. If serious ($18K budget), you're on them immediately."
              }
            ].map((use, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 border border-gray-100 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group">
                <h3 className="text-2xl font-bold text-gray-900 mb-3 transition-colors duration-300 group-hover:text-amber-600">{use.title}</h3>
                <p className="text-gray-700 text-lg transition-colors duration-300 group-hover:text-gray-900">{use.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              What Hardscape Contractors Are Saying
            </h2>
            <p className="text-xl text-gray-600">From small crews to established hardscape companies</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-amber-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-amber-200">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <StarIcon key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.quote}"</p>
                <div className="border-t border-amber-200 pt-6">
                  <div className="font-bold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-600 mb-3">{testimonial.company}</div>
                  <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm font-semibold">
                    <CheckCircleIcon className="h-4 w-4" />
                    {testimonial.stat}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              The Math on Missing One Call
            </h2>
          </div>

          <div className="bg-white rounded-3xl p-12 border-2 border-amber-200 shadow-xl">
            <div className="grid md:grid-cols-5 gap-6 text-center mb-8">
              <div>
                <div className="text-4xl font-bold text-amber-600 mb-2">1</div>
                <div className="text-gray-700 font-semibold">High-ticket call</div>
              </div>
              <div className="flex items-center justify-center">
                <div className="text-2xl text-gray-400">×</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-amber-600 mb-2">$18K</div>
                <div className="text-gray-700 font-semibold">Average deck project</div>
              </div>
              <div className="flex items-center justify-center">
                <div className="text-2xl text-gray-400">=</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-red-600 mb-2">$18K</div>
                <div className="text-gray-700 font-semibold">Lost to competitor</div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-8 mt-8">
              <p className="text-center text-lg font-semibold text-gray-900 mb-4">
                If you miss just 2 high-value calls per month:
              </p>
              <div className="text-center">
                <div className="text-5xl font-bold text-red-600 mb-2">$432,000</div>
                <div className="text-gray-700">Annual revenue loss</div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-2xl p-8 mt-8 text-center text-white">
              <p className="text-sm font-semibold text-amber-50 uppercase mb-3">Greet costs:</p>
              {growthDisplayPrice.showSale && (
                <div className="text-xl font-semibold text-amber-100 line-through mb-1">
                  {growthDisplayPrice.basePrice}
                </div>
              )}
              <div className="text-4xl font-bold mb-2">{growthDisplayPrice.price}</div>
              {growthDisplayPrice.showSale && growthDisplayPrice.label && (
                <div className="mb-2 inline-flex rounded-full bg-white/20 px-3 py-1 text-xs font-semibold text-white">
                  {growthDisplayPrice.label}
                </div>
              )}
              <p className="text-amber-50">Greet pays for itself in one call</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Questions Hardscape Contractors Ask
            </h2>
          </div>

          <div className="space-y-4">
            {hardscapeContractorsFaqs.map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                <button
                  className="w-full px-6 py-5 text-left flex justify-between items-center hover:bg-gray-100 transition-colors"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <ChevronDownIcon 
                    className={`h-5 w-5 text-gray-500 transform transition-transform ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <div className={`px-6 text-gray-600 border-t border-gray-200 bg-white transition-all duration-300 ${
                  openFaq === index ? 'pb-5 max-h-96' : 'pb-0 max-h-0'
                } overflow-hidden`}>
                  {faq.answer}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative bg-gradient-to-br from-slate-900 via-amber-900 to-slate-900 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-red-500/10"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 blur-3xl"></div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Never Miss a High-Ticket Hardscape Call Again
          </h2>
          <p className="text-xl text-amber-50 mb-12 max-w-2xl mx-auto">
            Start capturing every deck quote, patio inquiry, and emergency repair call-and stop watching competitors answer the phone first.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link
              href="/register"
              onClick={() => trackButtonClick("hardscape_cta_final", "hardscape_page")}
              className="bg-white text-amber-600 hover:bg-gray-50 px-10 py-5 rounded-2xl font-bold text-lg transition-all inline-flex items-center justify-center shadow-xl"
            >
              Start Free Trial
              <ArrowRightIcon className="h-5 w-5 ml-2" />
            </Link>
            <a
              href="tel:+14158773166"
              onClick={() => trackPhoneClick('+14158773166', 'hardscape_cta_final')}
              className="bg-amber-500 hover:bg-amber-600 text-white px-10 py-5 rounded-2xl font-bold text-lg transition-all inline-flex items-center justify-center"
            >
              See Live Demo
            </a>
          </div>
          <p className="text-amber-100 text-sm">
            ✓ 7-day free trial • ✓ 15-minute setup
          </p>
        </div>
      </section>
    </LandingLayout>
  );
}

